import { skipToken, UseQueryResult } from "@tanstack/react-query";
import { rewind } from "@turf/turf";
import {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry,
} from "geojson";
import { isEqual } from "lodash-es";
import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMediaQuery } from "react-responsive";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  contentType,
  FieldWithCurrentBoundary,
  getCombinedLayersForSource,
  getFieldsByFarm,
  getInternalAllFarms,
  getMyUser,
  getPublishedSystemContent,
  getSingleLayerForSource,
  getSingleLayerForSourceWithDate,
  listDatesForLayer,
  listFarmPublishedSections,
  listFarmSections,
  listPublishedSections,
  listSections,
  moduleType,
  FeatureCollection as TsClientFeatureCollection,
} from "regen-api-ts-client";

import { JSONAPIError } from "../api/apiConfiguration";
import {
  CombinedLayerDatesourceId,
  DatasourceId,
  DATASOURCES,
  isSingleLayerType,
  isSingleLayerWithDateType,
  LayerId,
  SingleLayerDatasourceId,
  SingleLayerWithDateDatasourceId,
} from "../components/InteractiveMap/types";
import { useLoadedFarmData } from "../pages/DataProvider";
import { mediaQueries } from "../style-constants";
import { AppRoleType, PermissionType } from "../types";
import { useRegenQuery } from "./apiHooks";
import { FARM_WIDE_FIELD } from "./constants";
import { isSatelliteImageryEnabled } from "./featureFlags";
import {
  calculateBBox,
  downloadFile,
  featureCollectionFromFieldList,
  hasAllRoles,
  hasSomeAppPermissions,
  hasSomeRoles,
  sortCaseInsensitiveFactory,
} from "./functions";

const MUTATION_THROTTLE_TIME = 3000; //ms

export type FormValue = string | number | boolean | string[];

export type FormState = {
  [key: string]: FormValue;
};

export type FormErrors = {
  [key: string]: string | undefined;
};

type ValidateFunction<T extends FormState> = (formState: T) => FormErrors;
type SubmissionSuccessCallback<T extends FormState> = (formState: T) => void;

export const useForm = <T extends FormState>(
  initialState: T,
  validate: ValidateFunction<T>,
  onSubmissionSuccess: SubmissionSuccessCallback<T>,
) => {
  const [formState, setFormState] = useState<T>(initialState);
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const hasFormErrors = useMemo(
    () => Object.values(formErrors).some((error) => !!error),
    [formErrors],
  );

  // Updates the state if we switch between forms (like when switching between field info
  // on the Field List page)
  useEffect(() => {
    setFormState(initialState);
  }, [initialState, setFormState]);

  useEffect(() => {
    // Begin validation on every change after the user has tried to submit a form with errors.
    if (hasFormErrors) {
      const errorsForNewFormState = validate(formState);
      // Check for equality here so that formErrors doesn't get updated (thus updating
      // hasFormErrors) on every state change.
      if (!isEqual(formErrors, errorsForNewFormState)) {
        setFormErrors(errorsForNewFormState);
      }
    }
  }, [formErrors, formState, hasFormErrors, validate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormState((prevState) => ({ ...prevState, [name]: checked }));
  };

  const handleSelectChange = (value: FormValue, name: string) => {
    setFormState((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleValueChange = (key: keyof T, value: FormValue) => {
    setFormState((prevState) => ({ ...prevState, [key]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validate(formState);
    setFormErrors(errors);
    if (!Object.values(errors).some((error) => !!error)) {
      onSubmissionSuccess(formState);
    }
  };

  return {
    formState,
    formErrors,
    hasFormErrors,
    handleInputChange,
    handleCheckboxChange,
    handleSelectChange,
    handleValueChange,
    handleSubmit,
  };
};

/**
 * Return a sorted list of fields for a farm for an annum
 * @param annum
 * @param farmId
 * @returns
 */
export const useFields = (farmId: string, annum?: number) => {
  const query = useRegenQuery(
    getFieldsByFarm,
    annum
      ? {
          path: { annum: annum, farm_id: farmId },
        }
      : skipToken,
  );

  return {
    fields: query.data?.sort(sortCaseInsensitiveFactory("name")),
    ...query,
    query,
  };
};

/**
 * Given a field list, convert them to a feature collection (memoized),
 * which is useful for displaying on the map/using map draw tools. Calculates
 * bounding box as well, for conveniance.
 */
export const useFieldsFeatureCollection = (
  fields: FieldWithCurrentBoundary[] = [],
) => {
  const featureCollection: FeatureCollection = useMemo(() => {
    if (!fields?.length) {
      return {
        type: "FeatureCollection",
        features: [],
      };
    }
    const collection = featureCollectionFromFieldList(fields);
    // bbox is used enough to calculate it here each time fields change
    collection.bbox = calculateBBox(collection);
    return collection;
  }, [fields]);

  return featureCollection;
};

export const useRetryQueryUntilChange = (
  query: UseQueryResult<unknown, Error>,
  maxAttempts: number,
  refetchInterval: number,
) => {
  const refetchWithDelay = (attemptsLeft: number, previousData: unknown) => {
    if (attemptsLeft > 0) {
      setTimeout(async () => {
        const { data: newData } = await query.refetch();

        try {
          if (JSON.stringify(newData) !== JSON.stringify(previousData)) {
            console.log("Data has changed, stopping refetch.");
            return; // Stop refetching since the data has changed
          } else {
            console.log("Data has not changed, attempting to refetch.");
            refetchWithDelay(attemptsLeft - 1, newData); // Continue refetching
          }
        } catch {
          return;
        }
      }, refetchInterval);
    }
  };

  const startRefetch = () => {
    refetchWithDelay(maxAttempts, query.data);
  };

  return { ...query, startRefetch };
};

export const useGetMyUser = () => useRegenQuery(getMyUser, {});

export const useHasSomeRoles = (roles: AppRoleType[]) => {
  const { data: me, isLoading, isFetched } = useGetMyUser();
  return { data: hasSomeRoles(me, roles), isLoading, isFetched, me };
};

export const useHasSomeAppPermissions = (permissions: PermissionType[]) => {
  const { data: me, isLoading } = useGetMyUser();
  return { data: hasSomeAppPermissions(me, permissions), isLoading, me };
};

export const useHasAllRoles = (roles: AppRoleType[]) => {
  const { data: me, isLoading } = useGetMyUser();
  return { data: hasAllRoles(me, roles), isLoading, me };
};

export const useIsWidescreen = () => useMediaQuery(mediaQueries.widescreen);

export const useListPublishedRecommendations = (
  farmId: string,
  fieldId: string = FARM_WIDE_FIELD.id,
  annum: number,
  moduleId: moduleType,
) => {
  const isFarmWide = fieldId === FARM_WIDE_FIELD.id;
  const { data: publishedFarmSections, isPending: areFarmSectionsPending } =
    useRegenQuery(
      listFarmPublishedSections,
      isFarmWide
        ? {
            path: {
              farm_id: farmId,
              annum,
              module_type: moduleId,
            },
          }
        : skipToken,
    );

  const { data: publishedSections, isPending: areFieldSectionsPending } =
    useRegenQuery(
      listPublishedSections,
      !isFarmWide
        ? {
            path: {
              farm_id: farmId,
              field_id: fieldId,
              annum,
              module_type: moduleId,
            },
          }
        : skipToken,
    );

  return isFarmWide
    ? {
        publishedRecommendations: publishedFarmSections,
        isPending: areFarmSectionsPending,
      }
    : {
        publishedRecommendations: publishedSections,
        isPending: areFieldSectionsPending,
      };
};

export const useGetSystemContent = (annum: number, contentId: contentType) => {
  const { error, data, isLoading } = useRegenQuery(
    getPublishedSystemContent,
    contentId
      ? {
          path: {
            annum,
            content_id: contentId,
          },
          throwOnError: false,
        }
      : skipToken,
  );

  // If an error other than 404 (expected) is returned, show a toast
  useEffect(() => {
    if (error && (error as JSONAPIError)?.status != 404) {
      toast.error(error.message);
    }
  }, [error]);

  return { data: data?.content, isLoading };
};

export const useListInternalSections = (
  farmId: string,
  fieldId: string = FARM_WIDE_FIELD.id,
  annum: number,
  moduleId: string | undefined,
) => {
  const isFarmWide = fieldId === FARM_WIDE_FIELD.id;

  const {
    data: farmSectionsForModule,
    isLoading: areFarmSectionsLoading,
    isFetching: isFetchingFarmSections,
  } = useRegenQuery(
    listFarmSections,
    !!moduleId && isFarmWide
      ? {
          path: {
            farm_id: farmId,
            annum: annum,
            module_type: moduleId as moduleType,
          },
        }
      : skipToken,
  );

  const {
    data: fieldSectionsForModule,
    isLoading: areFieldSectionsLoading,
    isFetching: isFetchingFieldSections,
  } = useRegenQuery(
    listSections,
    !!moduleId && !isFarmWide
      ? {
          path: {
            farm_id: farmId,
            field_id: fieldId,
            annum,
            module_type: moduleId as moduleType,
          },
        }
      : skipToken,
  );
  return isFarmWide
    ? {
        sectionsForModule: farmSectionsForModule,
        isLoading: areFarmSectionsLoading,
        isFetching: isFetchingFarmSections,
      }
    : {
        sectionsForModule: fieldSectionsForModule,
        isLoading: areFieldSectionsLoading,
        isFetching: isFetchingFieldSections,
      };
};

/**
 * Hook that will call the provided mutation function, but throttled to only
 * at the end of MUTATION_THROTTLE_TIME ms. This is useful for saving content
 * automatically when `onChange` events are fired with throttledMutate,
 * but having an ability to cancel any throttled saves and save immediately
 * by calling immediateMutate.
 * @returns
 */
export const useThrottleMutation = <T, U extends unknown[]>(
  mutationFunc: (...args: U) => T,
) => {
  // Track timeouts that we might need to reset.
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [mutatePending, setMutatePending] = useState<boolean>(false);

  // Called when content changes or
  const throttledMutate = useCallback(
    (...args: U) => {
      setMutatePending(true);
      function saveContent() {
        mutationFunc(...args);
        setMutatePending(false);
      }
      // clear any previous timeout - extending the time before next
      // mutation on every call to throttledMutate
      timeoutRef.current && clearTimeout(timeoutRef.current);

      // set a new timeout
      timeoutRef.current = setTimeout(saveContent, MUTATION_THROTTLE_TIME);
    },
    [mutationFunc, setMutatePending],
  );

  const immediateMutate = useCallback(
    (...args: U) => {
      setMutatePending(true);
      function saveContent() {
        mutationFunc(...args);
        setMutatePending(false);
      }
      // clear any previous timeout (clearing any throttled mutations)
      timeoutRef.current && clearTimeout(timeoutRef.current);

      // set a new timeout for immediate mutation
      timeoutRef.current = setTimeout(saveContent, 0);
    },
    [mutationFunc, setMutatePending],
  );

  return {
    throttledMutate,
    immediateMutate,
    mutatePending,
  };
};

// Quick and hacky way to export farm and field data to a CSV file to help
// with coordination efforts on product, ops, and agronomy teams.
export const useExportFarmAndFieldData = () => {
  const { data: farmList } = useRegenQuery(getInternalAllFarms, {
    query: { "soil-lab": false, centroid: false },
  });

  const handleExportFarmAndFieldData = async () => {
    const csv = [
      "farm_id,farm_name,customer_name,field_id_2025,field_name_2025,field_enrolled_2025,field_irrigation_2025,field_id_2024,field_name_2024,field_enrolled_2024,field_irrigation_2024",
    ];

    await Promise.all(
      farmList?.map(async (farm) => {
        const { data: fields2025 } = await getFieldsByFarm({
          path: { farm_id: farm.id, annum: 2025 },
          throwOnError: true,
        });
        csv.push(
          ...fields2025.map(
            (field) =>
              `${farm.id},${farm.label},${farm.name},${field.id},${field.name},${field.enrolled},${field.irrigation},,,,`,
          ),
        );
        const { data: fields2024 } = await getFieldsByFarm({
          path: { farm_id: farm.id, annum: 2024 },
          throwOnError: true,
        });
        csv.push(
          ...fields2024.map(
            (field) =>
              `${farm.id},${farm.label},${farm.name},,,,,${field.id},${field.name},${field.enrolled},${field.irrigation}`,
          ),
        );
      }) || [],
    );

    const blob = new Blob([csv.join("\n")], { type: "text/csv" });
    downloadFile(
      blob,
      `farm_and_field_data-${new Date().toLocaleDateString()}.csv`,
    );
  };
  return handleExportFarmAndFieldData;
};

/**
 * Hook that will detect clicks outside the bounds of an element and
 * trigger a callback function.
 */
export const useClickOutside = (
  ref: RefObject<HTMLElement | undefined>,
  onClickOutside: () => void,
  addEventListener = false,
) => {
  const handleClick = (event: MouseEvent) => {
    if (ref.current && !ref.current.contains(event.target as HTMLElement)) {
      onClickOutside();
    }
  };

  useEffect(() => {
    if (addEventListener) {
      document.addEventListener("click", handleClick);
    }

    return () => {
      document.removeEventListener("click", handleClick);
    };
  });
};

// Rewind a provided geojson feature collection to comply with mapbox constraints and return
// information about fields with no data.
const useRewindGeoJson = (data: TsClientFeatureCollection) => {
  const rewoundGeoJson = useMemo(() => {
    return rewind({
      type: "FeatureCollection",
      features: data.features as Feature<Geometry, GeoJsonProperties>[],
    });
  }, [data]);
  return rewoundGeoJson;
};

// Get a set of features for a layer in a datasource. If data is not available for a field,
// get the boundary features for that field.
export const useGetFeaturesForData = (
  data: TsClientFeatureCollection,
  fields: FieldWithCurrentBoundary[],
) => {
  const geojson = useRewindGeoJson(data);

  // Check whether there are any fields with no geojson data
  let noDataFields: FieldWithCurrentBoundary[] = [];
  if (geojson && "features" in geojson && geojson.features.length > 0) {
    // Get field ids of unique fields with data
    let fieldIdsWithData = geojson.features.map((f) => f.properties?.field_id);
    // Dedupe field ids here by converting to a Set and then back to an array.
    fieldIdsWithData = [...new Set(fieldIdsWithData)];
    // If the fields with data is equal to the number of fields, all fields have data
    // TODO: is this still true once we adjust enrolled/unenrolled fields?
    if (fieldIdsWithData.length !== fields.length) {
      // Get the fields with no geojson data
      noDataFields = fields.filter(
        (field) => !fieldIdsWithData.includes(field.id),
      );
    }
  } else {
    // rewind did not yield valid geojson or there were no valid features
    noDataFields = fields;
  }
  const noDataFieldFeatures = useFieldsFeatureCollection(noDataFields);

  return {
    geojson,
    noDataFieldFeatures:
      noDataFieldFeatures.features.length > 0 ? noDataFieldFeatures : undefined,
  };
};

// Create a combined object of current path and query params used in map navigation.
export const useMapParams = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  return { params, searchParams };
};

export const useIsOnline = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [setIsOnline]);

  return isOnline;
};

export const useGetDatesForLayer = (
  farmId: string,
  annum: number,
  datasourceId: DatasourceId,
  layerId?: LayerId,
) => {
  const { data } = useRegenQuery(
    listDatesForLayer,
    isSatelliteImageryEnabled &&
      datasourceId === DATASOURCES.satellite_imagery.id &&
      !!layerId &&
      isSingleLayerWithDateType(layerId)
      ? {
          path: {
            farm_id: farmId,
            annum,
            source: datasourceId as SingleLayerWithDateDatasourceId,
            layer: layerId,
          },
        }
      : skipToken,
  );
  return data ? data.dates : [];
};

export const useGetCombinedLayerData = (
  farmId: string,
  annum: number,
  datasourceId: DatasourceId,
) => {
  const { data, isLoading } = useRegenQuery(
    getCombinedLayersForSource,
    datasourceId === DATASOURCES.nutrient_testing.id ||
      datasourceId === DATASOURCES.fertilizer_recommendation.id
      ? {
          path: {
            farm_id: farmId,
            annum,
            source: datasourceId as CombinedLayerDatesourceId,
          },
        }
      : skipToken,
  );
  return { data, isLoading };
};

export const useGetSingleLayerData = (
  farmId: string,
  annum: number,
  datasourceId: DatasourceId,
  layerId?: LayerId,
) => {
  const { data, isLoading } = useRegenQuery(
    getSingleLayerForSource,
    datasourceId === DATASOURCES.ssurgo.id &&
      !!layerId &&
      isSingleLayerType(layerId)
      ? {
          path: {
            farm_id: farmId,
            annum,
            source: datasourceId as SingleLayerDatasourceId,
            layer: layerId,
          },
        }
      : skipToken,
  );
  return { data, isLoading };
};

export const useGetDateLayerData = (
  farmId: string,
  annum: number,
  datasourceId: DatasourceId,
  layerId?: LayerId,
  date?: string,
) => {
  const { data, isLoading } = useRegenQuery(
    getSingleLayerForSourceWithDate,
    isSatelliteImageryEnabled &&
      datasourceId === DATASOURCES.satellite_imagery.id &&
      !!layerId &&
      isSingleLayerWithDateType(layerId) &&
      !!date
      ? {
          path: {
            farm_id: farmId,
            annum,
            source: datasourceId as SingleLayerWithDateDatasourceId,
            layer: layerId,
            date,
          },
        }
      : skipToken,
  );
  return { data, isLoading };
};

// Month the "latest" annum (AKA the annum farthest into the future, displayed on the Field list
// map) rolls over to the next year.
const LATEST_ANNUM_ROLLOVER_MONTH = 4;
// Month the "current" annum (AKA the default annum displayed in modules) rolls over to the next
// year.
const CURRENT_ANNUM_ROLLOVER_MONTH = 7;

// Returns the default (current) annum to display to the user, given a "latest" annum value.
// Default annum should be the current year from July - Mar and the previous year from Apr - Jun
// For example, if the current crop year is '25-'26, the default should be '24-'25 until
// Jul 1, 2025. At this point, the default will switch to '25-'26. The latest annum will already
// have been '25-'26 because it gets updated on April 1.
export const getDefaultAnnum = (annumList: number[], latestAnnum: number) => {
  const date = new Date();
  const currentMonth = date.getUTCMonth() + 1; // getUTCMonth starts indexing from 0
  if (
    currentMonth >= LATEST_ANNUM_ROLLOVER_MONTH &&
    currentMonth < CURRENT_ANNUM_ROLLOVER_MONTH
  ) {
    // Default to the previous annum as long as it exists in the annum list. Otherwise default to the
    // latest annum.
    const prevAnnum = latestAnnum - 1;
    if (annumList.includes(prevAnnum)) {
      return prevAnnum;
    }
  }
  return latestAnnum;
};

/**
 * Return farm id and annum information for the current farm. Includes:
 * - urlAnnum: the parsed integer annum from the URL params
 * - currentAnnum: the url annum or default (depends on growing season), if no annum in url
 * - latestAnnum: (from the server) latest annum
 * - annumList: of all annums (from the server)
 */
export const useFarmData = () => {
  const {
    farm: { id: farmId },
    annums: { annums, latestAnnum },
  } = useLoadedFarmData();
  const { annum: urlAnnumRaw } = useParams();
  const urlAnnum = (!!urlAnnumRaw && +urlAnnumRaw) || undefined;
  const defaultAnnum = getDefaultAnnum(annums, latestAnnum);

  return {
    farmId,
    urlAnnum,
    currentAnnum: urlAnnum || defaultAnnum,
    latestAnnum,
    annumList: annums,
  };
};
